import lodash from "lodash";
import { BANK_ACCOUNT_TYPE, PAYMENT_METHOD_TYPE, SUPPORTED_COUNTRIES } from "../../constants";
import { BT_CARD_BRANDS, BT_CARD_TYPES } from "../../schemas/cardInfoSchema";
import { buildValidationObject, ERROR_CODES, ERROR_TYPES, Errors, getSafeStyles, parseTheme } from "../../util";
import { buildBillingInfoSchema } from "../../schemas/billingInfoSchema";
import { ODIN_INPUT_CLASSNAME } from "../../util/constants";

/**
 * All possible Basis Theory fields for card.
 * @readonly
 */
export const BasisTheoryTokenType = Object.freeze({
	CARD: "card",
	BANK: "bank",
	TOKEN: "token"
});

/**
 * All possible Basis Theory fields for bank account.
 * @readonly
 */
export const BankAccountFields = Object.freeze({
	ROUTING_NUMBER: "routingNumber",
	ACCOUNT_NUMBER: "accountNumber"
});

export const BASIS_THEORY_TARGETS = Object.freeze({
	CARD_NUMBER: "cardNumber",
	EXPIRATION_DATE: "expirationDate",
	CVC: "cvc"
});

/**
 * All possible Basis Theory Elements we plan on using.
 * @enum {string}
 */
export const BasisTheoryElement = Object.freeze({
	CARD: "card",
	TEXT: "text"
});

/**
 * initialize and mount our Basis Theory card element.
 * @async
 * @type {function} initBankAccountFields
 * @param {BasisTheory} bt - reference to our instance of Basis Theory.
 * @param {BasisTheoryElement} btElement - reference to our instance of Basis Theory.
 * @param {btCardConfig} config - options for end user to plug into their Basis Theory element.
 * @param {odinTheme} theme - theme, used here to add styles to the parent div or input itself.
 * @param maskConfig - contains the mask data of required fields such as transit,institution and routing number.
 * @param onChangeValidation - used to check the change event complete attribute for validation
 * @return {Promise<*>}
 */
export const initBasisTheoryField = async (
	bt,
	btElement,
	{ config, theme, validation, fieldName },
	onChangeValidation
) => {
	const { selector, ariaLabel, ...other } = config;

	let createElementConfig = {
		...other,
		style: { base: theme }
	};

	if (btElement === BasisTheoryElement.CARD) {
		createElementConfig = {
			...createElementConfig,
			autoComplete: {
				number: "cc-number",
				expirationDate: "cc-exp",
				csc: "cc-csc"
			}
		};
	}

	if (btElement === BasisTheoryElement.TEXT) {
		createElementConfig = {
			...createElementConfig,
			targetId: selector
		};
		if (validation) {
			createElementConfig = { ...createElementConfig, validation };
		}
		if (ariaLabel) {
			createElementConfig = { ...createElementConfig, "aria-label": ariaLabel };
		}
	}

	try {
		const field = bt.createElement(btElement, createElementConfig);
		const holderEl = document.getElementById(selector);
		field.on("change", (changeEvent) => {
			if (changeEvent.errors?.length) {
				const validationResponse = buildValidationObject({
					type: ERROR_TYPES.FIELD_VALIDATION,
					isValid: false,
					errorCode:
						btElement === BasisTheoryElement.TEXT
							? ERROR_CODES.INVALID_BANK_INFORMATION
							: ERROR_CODES.INVALID_CARD_INFORMATION,
					fieldName,
					selector,
					isComplete: changeEvent.complete
				});
				onChangeValidation(validationResponse);
			} else {
				const validationResponse = buildValidationObject({
					type: ERROR_TYPES.FIELD_VALIDATION,
					isValid: true,
					fieldName,
					selector,
					isComplete: changeEvent.complete
				});
				onChangeValidation(validationResponse);
			}
		});

		await field.mount(`#${holderEl.getAttribute("id")}`);
		return field;
	} catch (e) {
		throw new Error(e);
	}
};

const getTokenConfigForBankAccount = (data, country) => {
	let fingerprintExpression;
	let mask;
	switch (country) {
		case SUPPORTED_COUNTRIES.US:
			fingerprintExpression = "{{ data.routing_number }} | {{ data.account_number }}";
			break;
		case SUPPORTED_COUNTRIES.CA:
			fingerprintExpression = "{{ data.transit_number }} | {{ data.institution_number }} | {{ data.account_number }}";

			mask = {
				transit_number: "{{ data.transit_number }}",
				institution_number: "{{ data.institution_number }}",
				account_number: "{{ data.account_number | reveal_last: 4 }}"
			};
			break;
		default:
			throw new Error("Unable to create token");
	}

	// BasisTheory does not support canadian bank accounts tokenization,
	// so we are using the general token type to store canadian bank accounts
	return {
		type: country === SUPPORTED_COUNTRIES.US ? BasisTheoryTokenType.BANK : BasisTheoryTokenType.TOKEN,
		data,
		...(mask && { mask }),
		fingerprint_expression: fingerprintExpression
	};
};

/**
 * @typedef {object} bankAccountDataPayload
 * @property {TextElement} routing_number - the Basis Theory text element used for routing number.
 * @property {TextElement} account_number - the Basis Theory text element used for account number.
 */

/**
 * Take flattened billing information object and parse it into the correct format for the Odin payload
 * @type {function} parseBillingInformation
 * @param {(unparsedBillingInformation|{})} billingInformation - the raw data collected from the user.
 * @returns {billingInformation} - the parsed data used in the Odin payload
 */
export const parseBillingInformation = (billingInformation) => {
	let parsedData = { address: {} };
	const addressKeys = ["addressLine1", "addressLine2", "city", "state", "postalCode", "country"];
	Object.entries(billingInformation).forEach(([key, value]) => {
		if (addressKeys.includes(key)) {
			parsedData = {
				...parsedData,
				address: { ...parsedData.address, [key]: value }
			};
		} else {
			parsedData = { ...parsedData, [key]: value };
		}
	});
	return parsedData;
};

/**
 * Tokenize the bank account for payment processing.
 * @param {Object} bt - The payment gateway instance.
 * @param {object} btField - The name of the bank account field.
 * @param {Object} billingInformation - The billing information associated with the bank account.
 * @param {string} country - The country of the bank account.
 * @return {Promise<object>} - The tokenized bank account object.
 */
export async function tokenizeBankAccount(bt, btField, billingInformation, country) {
	const { bankAccountType, ...parsedBillingInformation } = parseBillingInformation(billingInformation);
	const tokenPayload = getTokenConfigForBankAccount(btField, country);
	const { id, fingerprint, data } = await bt.tokenize(tokenPayload);
	return {
		id: fingerprint,
		token: id,
		type: PAYMENT_METHOD_TYPE.BANK_ACCOUNT,
		billingInformation: parsedBillingInformation,
		bankAccountType,
		...lodash.mapKeys(data, (value, key) => {
			return lodash.camelCase(key);
		}),
		country
	};
}

/**
 * Tokenize the credit ir debit card for payment processing.
 * @param {Object} bt - The payment gateway instance.
 * @param {string} btField - The name of the bank account field.
 * @param {Object} billingInformation - The billing information associated with the bank account.
 * @return {Promise<Object>} - The tokenized bank account object.
 */
export async function tokenizeCard(bt, btField, billingInformation) {
	const { bankAccountType, ...parsedBillingInformation } = parseBillingInformation(billingInformation);
	const tokenPayload = {
		type: BasisTheoryTokenType.CARD,
		data: btField,
		fingerprint_expression: "{{ data.number }}",
		mask: {
			number: "{{ data.number | reveal: 6, 4, 'X' }}",
			expiration_month: "{{ data.expiration_month }}",
			expiration_year: "{{ data.expiration_year }}"
		}
	};

	const {
		id,
		fingerprint,
		data,
		enrichments: { bin_details: binDetails }
	} = await bt.tokenize(tokenPayload);
	return {
		id: fingerprint,
		type: binDetails.type === BT_CARD_TYPES.Debit ? PAYMENT_METHOD_TYPE.DEBIT_CARD : PAYMENT_METHOD_TYPE.CREDIT_CARD,
		billingInformation: parsedBillingInformation,
		binDetails,
		accountNumber: data.number,
		expirationDate: `${data.expiration_month}/${data.expiration_year}`,
		token: id,
		cardBrand: BT_CARD_BRANDS[binDetails.card_brand.toLowerCase()]
	};
}

/**
 * Attach a callback to the submit button.
 * @constructor
 * @param {submitButtonConfig} submitButtonConfig - The configuration for the submit button.
 * @param {submitEvent} onSubmitCallback - the callback to be fired when clicking the submit button.
 */
export const attachSubmitEvent = (submitButtonConfig, onSubmitCallback) => {
	// eslint-disable-next-line no-undef
	const button = document.getElementById(submitButtonConfig.selector);

	if (button) {
		button.addEventListener("click", onSubmitCallback);
	} else {
		console.error(Errors.BUTTON_MISSING);
	}
};

/**
 * Creates billing information fields from the configuration object and attaches them to HTML elements on the page.
 * Returns the billing input's input HTML references for future use in managing its state.
 * @param {string} name - name of the billing info input (must be same as key on payload)
 * @param {billingInfoFieldConfig} config - configuration for an Odin Pay billing information field.
 * @param {function} onChange - callback to attach to the input's keyup event.  updates BillingInfo object & checks for valid input
 * @param {odinTheme} theme - styles to apply to the input.
 * @returns {HTMLElement}
 * */

export const buildBillingInformationInput = ({ name, config, onChange, theme, onBlur }) => {
	const holderEl = document.getElementById(config.selector);
	const inputStyles = getSafeStyles(theme);
	const parsedInputStyles = parseTheme(inputStyles);

	if (name === "bankAccountType") {
		const selectEl = document.createElement("select");
		Object.values(BANK_ACCOUNT_TYPE).forEach((value) => {
			const option = document.createElement("option");
			option.value = value;
			option.text = value;
			selectEl.appendChild(option);
		});
		onChange("bankAccountType", BANK_ACCOUNT_TYPE.CHECKING);
		if (config.placeholder) {
			selectEl.setAttribute("placeholder", config.placeholder);
		}
		if (config.initialValue) {
			selectEl.value = config.initialValue;
		}

		if (config.ariaLabel) {
			selectEl.setAttribute("aria-label", config.ariaLabel);
		}

		selectEl.addEventListener("change", () => {
			onChange(name, selectEl.value);
		});

		selectEl.addEventListener("blur", () => {
			onBlur(name, selectEl.value);
		});

		selectEl.setAttribute("style", `${parsedInputStyles} height: 100%; width: 100%;`);

		holderEl.appendChild(selectEl);

		return { inputElement: selectEl };
	}
	// currently we send addressLine2 as a blank string even if it is empty.  all other inputs disallow blank strings.
	if (name === "addressLine2") {
		onChange("addressLine2", "");
	}
	const inputEl = document.createElement("input");
	inputEl.setAttribute("type", "text");
	/** set class to odin-input, so that we can attach certain pseudo element styles to the stylesheet referencing this class * */
	inputEl.setAttribute("class", ODIN_INPUT_CLASSNAME);
	if (config.placeholder) {
		inputEl.setAttribute("placeholder", config.placeholder);
	}
	if (config.initialValue) {
		inputEl.setAttribute("value", config.initialValue);
	}

	if (config.ariaLabel) {
		inputEl.setAttribute("aria-label", config.ariaLabel);
	}

	// text inputs need to subtract 10px to account for their default padding
	inputEl.setAttribute("style", `${parsedInputStyles} height: calc(100% - 10px); width: calc(100% - 10px);`);

	inputEl.addEventListener("keyup", () => {
		onChange(name, inputEl.value);
	});

	inputEl.addEventListener("blur", () => {
		onBlur(name, inputEl.value);
	});

	if (name === "country") {
		const autoCapitalize = () => {
			const p = inputEl.selectionStart;
			inputEl.value = inputEl.value.toUpperCase();
			inputEl.setSelectionRange(p, p);
		};
		inputEl.setAttribute("maxlength", "2");
		inputEl.addEventListener("input", autoCapitalize);
	}

	holderEl.appendChild(inputEl);
	return { inputElement: inputEl };
};

/**
 * @type {function} validateBillingInformation
 * @param {string[]} billingInformationKeys
 * @param {unparsedBillingInformation} billingInformation
 * @param {Object} billingAddressConfigs
 * @return {{isValid: boolean, errors: null}}
 */
export const validateBillingInformation = async (billingInformationKeys, billingInformation, billingAddressConfigs) => {
	const errors = [];
	try {
		await buildBillingInfoSchema(billingInformationKeys).validate(billingInformation, {
			abortEarly: false
		});
		return errors;
	} catch (err) {
		// Handle validation errors
		err?.inner?.forEach((error) => {
			if (error.path !== undefined) {
				const matchingConfig = billingAddressConfigs[error.path];
				errors.push(
					buildValidationObject({
						isValid: false,
						fieldName: error.path,
						selector: matchingConfig?.selector,
						errorCode: error.errors?.[0],
						isComplete: false
					})
				);
			}
		});
		return errors;
	}
};
/**
 * @typedef preValidateResponse
 * @param {boolean} isValid - is the
 * @param {string} errors - error message to return to the user in the submit callback.
 * @param {billingInfoInputs} validatedBillingInfoInputs - styled inputs after validation.
 */

/**
 * validates the fields before we attempt to tokenize or send any data.
 * @type {function} preValidate;
 * @param {object} billingAddressConfigs configurations for non-BT related billing fields.
 * @param {(unparsedBillingInformation|{})} billingInformation the billing information data to be verified.
 * @param {odinTheme} theme - style theme
 * @param {BtFields} btFields - Basis Theory fields
 * @param {(BasisTheoryTokenType.BANK|BasisTheoryTokenType.CARD)} [fieldType]
 * @returns {preValidateResponse}
 */
export const preValidateFields = async (billingAddressConfigs, billingInformation, theme, btFields, fieldType) => {
	const errors = await validateBillingInformation(
		Object.keys(billingAddressConfigs),
		billingInformation,
		billingAddressConfigs
	);
	Object.entries(btFields).forEach(([fieldName, { field, selector }]) => {
		const { complete, empty, maskSatisfied, valid } = field.metadata;

		if (empty || !complete || !valid || !maskSatisfied) {
			errors.push(
				buildValidationObject({
					isValid: false,
					fieldName,
					selector,
					errorCode:
						fieldType === BasisTheoryTokenType.BANK
							? ERROR_CODES.INVALID_BANK_INFORMATION
							: ERROR_CODES.INVALID_CARD_INFORMATION,
					isComplete: complete
				})
			);
		}
	});

	return errors;
};
